import Sentry from "@similarweb/sentry-logger";

import ParseEventStreamService from "app/pages/segments/redesigned-wizard/segment-wizard/services/streamParser/streamParserService";
import type { StreamEvent } from "app/pages/segments/redesigned-wizard/segment-wizard/services/streamParser/types";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { ResponseError } from "app/services/fetchService";
import customIndustryAgentService, {
    type CustomIndustryResult,
} from "app/services/industryAnalysis/customIndustryAgentService";
import SegmentsApiService from "app/services/segments/segmentsApiService";

import type { CreatedIndustry } from "../../CustomHooks/customIndustryAgent.types";
import { AGENT_STEPS } from "../../CustomHooks/customIndustryAgent.types";
import { UNCAPPED_SEGMENT_BUDGET } from "../segmentBudget";
import { inferCompletedStepsFromResult } from "./inferCompletedSteps";
import {
    classifiedDomainsFromUpdate,
    createdSegmentsFromUpdate,
    errorCodeFromState,
    industryFromState,
    isGenerateRulesUpdate,
    runIdFromMetadata,
    segmentsOverBudgetFromUpdate,
    stepIndexOf,
    topicSummaryFromUpdate,
    trafficFailuresFromUpdate,
} from "./streamParsing";
import {
    getCustomIndustryAgentRunStorageKey,
    hydrateSnapshotFromStorage,
    parsePersistedCustomIndustryAgentRun,
    persistCustomIndustryAgentRun,
    snapshotFromPersistedRun,
} from "./storage";
import type { CustomIndustryAgentRunSnapshot } from "./types";
import { IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT } from "./types";

const POLL_INTERVAL_MS = 5000;
// Backstop for the cases the agent's own verdict cannot cover: a run that dies before
// reporting one (a node raising, the server going away) leaves the result endpoint answering
// "still working" forever, and a stream that stays open but goes quiet reports nothing at all.
// Armed for the whole run rather than from the poll loop, so a wedged stream times out too.
// Generous against the ~5 min the UI promises, because overshooting only delays an error
// message while undershooting calls a working run failed. A run that already created its
// industry is handed over instead, since only the best-effort overview warm-up is left.
const RUN_DEADLINE_MS = 20 * 60 * 1000;

type Listener = () => void;

class CustomIndustryAgentRunService {
    private listeners = new Set<Listener>();

    private snapshot: CustomIndustryAgentRunSnapshot = IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT;

    private pollTimer: ReturnType<typeof setInterval> | null = null;

    private deadlineTimer: ReturnType<typeof setTimeout> | null = null;

    /** `startedAt` of the run the deadline above is armed for, so a new run re-arms it. */
    private deadlineArmedFor: number | null = null;

    private ssePromise: Promise<void> | null = null;

    private runAbortController: AbortController | null = null;

    /** Bumped whenever this tab starts, drops or adopts a run, so a continuation of an older
     *  one can tell it no longer owns the snapshot. */
    private execution = 0;

    private attachInFlight = false;

    /** After one attach attempt ends without a finished industry, fall back to polling only. */
    private streamRecoveryExhausted = false;

    private hydrated = false;

    private recoveryStarted = false;

    private initialLoadComplete = true;

    /** Thread whose created industry this tab already re-fetched the user's assets for. */
    private assetsRefreshedFor: string | null = null;

    constructor() {
        if (typeof window !== "undefined") {
            window.addEventListener("storage", this.handleStorageEvent);
        }
    }

    subscribe = (listener: Listener): (() => void) => {
        this.ensureHydrated();
        this.listeners.add(listener);
        // Recovering a run reaches the network, so it hangs off the subscription rather than
        // `getSnapshot` — React calls that one during render.
        this.startRecoveryOnce();

        return () => this.listeners.delete(listener);
    };

    getSnapshot = (): CustomIndustryAgentRunSnapshot => {
        this.ensureHydrated();
        return this.snapshot;
    };

    getInitialLoadComplete = (): boolean => {
        this.ensureHydrated();
        return this.initialLoadComplete;
    };

    isRunBlockingNewStart = (): boolean => this.snapshot.status === "generating" || this.snapshot.status === "done";

    generate = async (
        countryId: number,
        description: string,
        maxSegments: number = UNCAPPED_SEGMENT_BUDGET,
    ): Promise<void> => {
        this.ensureHydrated();

        if (this.isRunBlockingNewStart()) {
            return;
        }

        this.patchSnapshot({
            status: "generating",
            completedSteps: 0,
            discoveredDomains: null,
            topicSummary: null,
            trafficFailures: null,
            createdSegments: null,
            rulesGenerated: 0,
            maxSegments,
            segmentsOverBudget: 0,
            industry: null,
            errorCode: null,
            description,
            countryId,
            threadId: null,
            runId: null,
            startedAt: Date.now(),
        });
        this.streamRecoveryExhausted = false;

        const execution = ++this.execution;
        const controller = new AbortController();
        this.runAbortController = controller;

        // A thread on its own is not a run: recovery through `/result` only makes sense once the
        // agent has actually accepted the run, otherwise the endpoint answers "still working"
        // about a run that never existed and the loader spins until the deadline.
        let runStarted = false;

        try {
            const { threadId } = await customIndustryAgentService.createThread();
            // Cancelled or replaced while the thread was created: no run was started on it.
            if (execution !== this.execution) {
                return;
            }
            this.patchSnapshot({ threadId });

            const { response } = await customIndustryAgentService.runAgent(
                threadId,
                {
                    countryId,
                    description,
                    maxSegments,
                },
                {
                    cancellation: controller.signal,
                },
            );

            if (!response?.ok) {
                throw new Error(`Agent request failed: ${response?.status} ${response?.statusText}`);
            }

            runStarted = true;
            await this.beginStreamConsumption(response, execution);
        } catch (error) {
            if (execution !== this.execution) {
                return;
            }

            this.ssePromise = null;
            this.runAbortController = null;

            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }

            if (runStarted && this.snapshot.threadId) {
                Sentry.error("CustomIndustryAgent SSE disconnected", error);
                void this.resumeStreamOrPoll();
                return;
            }

            Sentry.error("CustomIndustryAgent failed", error);
            this.failRun();
        }
    };

    cancel = (): void => {
        this.runAbortController?.abort();
        this.runAbortController = null;
        this.attachInFlight = false;
        this.streamRecoveryExhausted = false;
        this.stopPolling();

        const { threadId, runId } = this.snapshot;
        this.dismiss();
        this.cancelOnServer(threadId, runId);
    };

    /** The run finished and created the industry; this client could not open it. Parks the
     *  run on an error that keeps the industry, so it can be opened instead of built again. */
    reportHandoffFailure = (): void => {
        if (this.snapshot.status !== "done") {
            return;
        }

        this.failRun();
    };

    /** An error is shown once: leaving the builder that shows it lets it go. */
    dismissError = (): void => {
        if (this.snapshot.status === "error") {
            this.dismiss();
        }
    };

    dismiss = (): void => {
        this.execution++;
        this.stopPolling();
        this.snapshot = IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT;
        this.notify();
    };

    private ensureHydrated(): void {
        if (this.hydrated) {
            return;
        }

        this.hydrated = true;
        this.snapshot = hydrateSnapshotFromStorage();
        this.initialLoadComplete = this.snapshot.status !== "generating";
    }

    private startRecoveryOnce(): void {
        if (this.recoveryStarted || this.snapshot.status !== "generating") {
            return;
        }

        this.recoveryStarted = true;
        this.syncRunDeadline();
        void this.resumeStreamOrPoll();
    }

    private beginStreamConsumption(response: Response, execution: number): Promise<void> {
        this.stopPolling();
        const consumption = this.consumeAgentStream(response, execution);
        this.ssePromise = consumption.then((errorCode) => {
            if (execution !== this.execution) {
                return;
            }

            this.ssePromise = null;
            this.runAbortController = null;
            this.finalizeStreamResult(errorCode);
        });

        return this.ssePromise;
    }

    /** Resolves to the code the agent rejected the prompt with, if it did. */
    private consumeAgentStream = async (response: Response, execution: number): Promise<string | null> => {
        let errorCode: string | null = null;

        await ParseEventStreamService.parseEventStream(response, {
            onEvent: (event: StreamEvent) => {
                if (execution !== this.execution) {
                    return;
                }

                this.handleStreamEvent(event, (code) => {
                    errorCode = code;
                });
            },
        });

        return errorCode;
    };

    private handleStreamEvent = (event: StreamEvent, onRejected: (errorCode: string) => void): void => {
        // The rest of a chunk still arrives after an error event ended the run.
        if (this.snapshot.status !== "generating") {
            return;
        }

        if (event.type === "metadata") {
            const runId = runIdFromMetadata(event.data);
            if (runId) {
                this.patchSnapshot({ runId });
            }
            return;
        }

        // The server reporting a failure, as JSON from the agent or as plain text when the
        // backend's own stream fails. Unlike a dropped connection, it is a verdict.
        if (event.type === "error") {
            this.failOnStreamError(event.data);
            return;
        }

        if (event.type === "updates") {
            const index = stepIndexOf(event.data);
            if (index >= 0) {
                this.patchSnapshot({ completedSteps: index + 1 });
            }

            const topicSummary = topicSummaryFromUpdate(event.data);
            if (topicSummary) {
                this.patchSnapshot({ topicSummary });
            }

            const trafficFailures = trafficFailuresFromUpdate(event.data);
            if (trafficFailures) {
                this.patchSnapshot({ trafficFailures });
            }

            const domains = classifiedDomainsFromUpdate(event.data);
            if (domains) {
                this.patchSnapshot({ discoveredDomains: domains });
            }

            const segmentsOverBudget = segmentsOverBudgetFromUpdate(event.data);
            if (segmentsOverBudget !== null) {
                this.patchSnapshot({ segmentsOverBudget });
            }

            const createdSegments = createdSegmentsFromUpdate(event.data);
            if (createdSegments) {
                this.patchSnapshot({ createdSegments });
            }

            if (isGenerateRulesUpdate(event.data)) {
                this.patchSnapshot({ rulesGenerated: this.snapshot.rulesGenerated + 1 });
            }

            if (AGENT_STEPS[index] === "warm_overview" && this.snapshot.industry) {
                this.handOffWhileTheRunWarmsOn(this.snapshot.industry);
            }
            return;
        }

        if (event.type === "values") {
            // Only recorded: the state names the industry from `publish_assets` on, while
            // `warm_overview` still runs, so the hand-off waits for that step to report.
            const industry = industryFromState(event.data);
            if (industry) {
                this.rememberIndustry(industry);
            }

            const errorCode = errorCodeFromState(event.data);
            if (errorCode) {
                onRejected(errorCode);
            }
        }
    };

    /** After `warm_overview` the run only warms what the Players page reads, which can take
     *  minutes and nobody needs to wait for. Stops reading, too: Pro starts the run so that it
     *  carries on without a listener. */
    private handOffWhileTheRunWarmsOn(industry: CreatedIndustry): void {
        this.runAbortController?.abort();
        this.runAbortController = null;
        this.completeRun(industry);
    }

    /** Stops reading as well, so a stream that keeps talking cannot touch a run started after. */
    private failOnStreamError(payload: unknown): void {
        const details = typeof payload === "string" ? payload : JSON.stringify(payload);
        Sentry.error(
            "CustomIndustryAgent stream error event",
            new Error(`threadId=${this.snapshot.threadId} ${details}`),
        );
        this.runAbortController?.abort();
        this.runAbortController = null;
        this.failRun();
    }

    private finalizeStreamResult = (errorCode: string | null): void => {
        if (this.snapshot.status !== "generating") {
            return;
        }

        const { industry } = this.snapshot;
        if (industry) {
            this.completeRun(industry);
            return;
        }

        if (errorCode) {
            this.rejectRun(errorCode);
            return;
        }

        this.fallbackToPolling();
    };

    private fallbackToPolling(): void {
        this.streamRecoveryExhausted = true;
        this.startPolling();
    }

    private resumeStreamOrPoll = async (): Promise<void> => {
        if (this.ssePromise || this.attachInFlight || this.snapshot.status !== "generating") {
            this.completeInitialLoad();
            return;
        }

        const stillGenerating = await this.syncFromServer();
        this.completeInitialLoad();

        if (!stillGenerating) {
            return;
        }

        await this.attachOrPoll();
    };

    private attachOrPoll = async (): Promise<void> => {
        if (this.ssePromise || this.attachInFlight) {
            return;
        }

        if (this.streamRecoveryExhausted) {
            this.startPolling();
            return;
        }

        const { threadId, runId } = this.snapshot;
        if (!threadId) {
            return;
        }

        if (!runId) {
            this.fallbackToPolling();
            return;
        }

        const { execution } = this;
        const controller = new AbortController();
        this.attachInFlight = true;
        this.runAbortController = controller;

        try {
            const { response } = await customIndustryAgentService.attachStream(threadId, runId, {
                cancellation: controller.signal,
            });

            if (execution !== this.execution) {
                return;
            }

            if (!response?.ok) {
                this.fallbackToPolling();
                return;
            }

            await this.beginStreamConsumption(response, execution);
        } catch (error) {
            if (execution !== this.execution) {
                return;
            }

            this.ssePromise = null;
            this.runAbortController = null;

            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }

            if (this.snapshot.status !== "generating") {
                return;
            }

            Sentry.error("CustomIndustryAgent stream attach failed", error);
            this.fallbackToPolling();
        } finally {
            this.attachInFlight = false;
        }
    };

    private handleStorageEvent = (event: StorageEvent): void => {
        const storageKey = getCustomIndustryAgentRunStorageKey();
        if (!storageKey || event.key !== storageKey) {
            return;
        }

        if (!event.newValue) {
            const { status, threadId } = this.snapshot;
            if (status === "generating" && threadId) {
                void this.reconcileRunClearedElsewhere(threadId);
                return;
            }

            this.resetFromOtherTab();
            return;
        }

        const persisted = parsePersistedCustomIndustryAgentRun(event.newValue);
        const adopted = persisted ? snapshotFromPersistedRun(persisted) : IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT;
        // Two tabs can both start a run before either has persisted it. The other tab's run
        // wins here, so this tab's own execution stops before its callbacks touch that one.
        if (adopted.threadId !== this.snapshot.threadId) {
            this.abandonLocalExecution();
        }
        this.snapshot = adopted;

        if (this.snapshot.status !== "generating") {
            this.stopPolling();
        }

        this.refreshUserAssetsOnceIfDone();
        this.notifyListenersOnly();
    };

    /** Another tab dropped the run this one shows as generating: the run failed or was
     *  cancelled there, and only the server can say which. A verdict settles the run here too,
     *  a run that is still going is followed from this tab, and one that stopped without a
     *  verdict was cancelled. */
    private async reconcileRunClearedElsewhere(threadId: string): Promise<void> {
        try {
            const result = await customIndustryAgentService.result(threadId);
            if (this.isStale(threadId) || this.snapshot.status !== "generating" || this.applyResult(result)) {
                return;
            }

            if (result.isRunning) {
                void this.attachOrPoll();
                return;
            }
        } catch (error) {
            Sentry.error("CustomIndustryAgent sync failed", error);
            if (!this.isStale(threadId) && this.snapshot.status === "generating") {
                this.failRunIfThreadIsGone(error);
            }
            return;
        }

        this.resetFromOtherTab();
    }

    private abandonLocalExecution(): void {
        this.execution++;
        this.runAbortController?.abort();
        this.runAbortController = null;
        this.ssePromise = null;
        this.attachInFlight = false;
        this.stopPolling();
    }

    private resetFromOtherTab(): void {
        this.snapshot = IDLE_CUSTOM_INDUSTRY_AGENT_RUN_SNAPSHOT;
        this.stopPolling();
        this.notifyListenersOnly();
    }

    private notifyListenersOnly(): void {
        this.syncRunDeadline();
        this.listeners.forEach((listener) => listener());
    }

    private completeInitialLoad(): void {
        if (this.initialLoadComplete) {
            return;
        }

        this.initialLoadComplete = true;
        this.notifyListenersOnly();
    }

    /** Keeps the over-budget count alive on the recovery path, where no stream update arrives. */
    private applySegmentsOverBudget(result: CustomIndustryResult): void {
        const { segmentsOverBudgetCount } = result;
        if (
            typeof segmentsOverBudgetCount !== "number" ||
            segmentsOverBudgetCount === this.snapshot.segmentsOverBudget
        ) {
            return;
        }

        this.patchSnapshot({ segmentsOverBudget: segmentsOverBudgetCount });
    }

    private patchSnapshot(patch: Partial<CustomIndustryAgentRunSnapshot>): void {
        this.snapshot = { ...this.snapshot, ...patch };
        this.notify();
    }

    private notify(): void {
        persistCustomIndustryAgentRun(this.snapshot);
        this.notifyListenersOnly();
    }

    /** Arms the run deadline for as long as a run is generating, and disarms it otherwise.
     *  The remaining time is measured from the run's own `startedAt`, so a run restored from
     *  storage that already outlived the deadline gives up on the spot. */
    private syncRunDeadline(): void {
        const { status, startedAt } = this.snapshot;
        if (status !== "generating" || startedAt == null) {
            this.clearRunDeadline();
            return;
        }

        if (this.deadlineTimer && this.deadlineArmedFor === startedAt) {
            return;
        }

        this.clearRunDeadline();
        this.deadlineArmedFor = startedAt;
        this.deadlineTimer = setTimeout(
            () => {
                this.deadlineTimer = null;
                this.abandonRun();
            },
            Math.max(0, startedAt + RUN_DEADLINE_MS - Date.now()),
        );
    }

    private clearRunDeadline(): void {
        this.deadlineArmedFor = null;
        if (!this.deadlineTimer) {
            return;
        }

        clearTimeout(this.deadlineTimer);
        this.deadlineTimer = null;
    }

    /** Gives up on a run that never reported a verdict, and stops it server-side too, so it
     *  cannot still publish an industry this client no longer waits for. A run that already
     *  created its industry hands it over instead. */
    private abandonRun(): void {
        if (this.snapshot.status !== "generating") {
            return;
        }

        this.runAbortController?.abort();
        this.runAbortController = null;

        const { industry } = this.snapshot;
        if (industry) {
            this.completeRun(industry);
            return;
        }

        const { threadId, runId } = this.snapshot;
        Sentry.error("CustomIndustryAgent run abandoned after the deadline", new Error(`threadId=${threadId}`));
        this.failRun();
        this.cancelOnServer(threadId, runId);
    }

    private cancelOnServer(threadId: string | null, runId: string | null): void {
        if (!threadId) {
            return;
        }

        void customIndustryAgentService.cancel(threadId, runId).catch((error) => {
            Sentry.error("CustomIndustryAgent cancel failed", error);
        });
    }

    private startPolling(): void {
        if (this.pollTimer || this.snapshot.status !== "generating") {
            return;
        }

        this.pollTimer = setInterval(() => {
            void this.pollOnce();
        }, POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (!this.pollTimer) {
            return;
        }

        clearInterval(this.pollTimer);
        this.pollTimer = null;
    }

    private async syncFromServer(): Promise<boolean> {
        const { threadId } = this.snapshot;
        if (!threadId || this.snapshot.status !== "generating") {
            return this.snapshot.status === "generating";
        }

        try {
            const result = await customIndustryAgentService.result(threadId);
            if (this.isStale(threadId) || this.snapshot.status !== "generating") {
                return false;
            }

            return !this.applyResult(result);
        } catch (error) {
            Sentry.error("CustomIndustryAgent sync failed", error);
            if (this.isStale(threadId) || this.snapshot.status !== "generating") {
                return false;
            }

            this.failRunIfThreadIsGone(error);
            return this.snapshot.status === "generating";
        }
    }

    /** A result that lands after the run was dismissed, cancelled or replaced belongs to a
     *  thread this client no longer shows, so applying it would overwrite the current run. */
    private isStale(threadId: string): boolean {
        return this.snapshot.threadId !== threadId;
    }

    /** Settles the run on the agent's verdict and reports whether the result carried one. */
    private applyResult(result: CustomIndustryResult): boolean {
        this.applySegmentsOverBudget(result);

        // The industry exists from `publish_assets` on, while `warm_overview` may still be running.
        if (result.industryId) {
            const industry = { industryId: result.industryId, industryName: result.industryName };
            if (!result.isRunning || result.overviewReady) {
                this.completeRun(industry);
                return true;
            }

            this.rememberIndustry(industry);
        }

        if (result.errorCode) {
            this.rejectRun(result.errorCode);
            return true;
        }

        if (result.success === false) {
            // Given up on without a code is an internal failure (e.g. every discovery source
            // down), not the prompt's fault.
            this.failRun();
            return true;
        }

        const inferredSteps = inferCompletedStepsFromResult(result);
        if (inferredSteps > this.snapshot.completedSteps) {
            this.patchSnapshot({ completedSteps: inferredSteps });
        }

        return false;
    }

    private rememberIndustry(industry: CreatedIndustry): void {
        if (industry.industryId !== this.snapshot.industry?.industryId) {
            this.patchSnapshot({ industry });
        }
    }

    private completeRun(industry: CreatedIndustry): void {
        this.patchSnapshot({
            industry,
            status: "done",
            completedSteps: AGENT_STEPS.length,
            errorCode: null,
        });
        this.stopPolling();
        this.refreshUserAssetsOnceIfDone();
    }

    /** The agent creates the industry and its segments server-side, while the picker and the
     *  segments metadata are cached once per SPA session — without a re-fetch, the industry is
     *  missing from the picker and its edit dialog drops every segment the cache lacks. */
    private refreshUserAssetsOnceIfDone(): void {
        const { status, industry, threadId } = this.snapshot;
        if (status !== "done" || !industry || !threadId || this.assetsRefreshedFor === threadId) {
            return;
        }

        this.assetsRefreshedFor = threadId;
        void SegmentsApiService.getInstance().refreshSegmentsMetaData();
        UserCustomCategoryService.refreshCustomCategories().catch((error) => {
            Sentry.error("CustomIndustryAgent failed to refresh custom categories", error);
        });
    }

    private rejectRun(errorCode: string): void {
        this.patchSnapshot({ status: "rejected", errorCode });
        this.stopPolling();
    }

    private failRun(): void {
        this.patchSnapshot({ status: "error" });
        this.stopPolling();
    }

    /** A thread the server no longer knows will never report a verdict, so waiting for the
     *  deadline would only keep the user on a loader. */
    private failRunIfThreadIsGone(error: unknown): void {
        if (error instanceof ResponseError && error.status === 404) {
            this.failRun();
        }
    }

    private async pollOnce(): Promise<void> {
        const { threadId } = this.snapshot;
        if (this.snapshot.status !== "generating" || !threadId) {
            this.stopPolling();
            return;
        }

        try {
            const result = await customIndustryAgentService.result(threadId);
            if (this.isStale(threadId) || this.snapshot.status !== "generating") {
                return;
            }

            this.applyResult(result);
        } catch (error) {
            Sentry.error("CustomIndustryAgent poll failed", error);
            if (!this.isStale(threadId) && this.snapshot.status === "generating") {
                this.failRunIfThreadIsGone(error);
            }
        }
    }
}

export const customIndustryAgentRunService = new CustomIndustryAgentRunService();
